import React from 'react';
import { useAppSelector, formatDates } from '../../app/hooks';
import strings, { selectLanguage } from '../localize/localizeSlice';

const SummaryDisplay: React.FC = () => {
  const { userInfoData, pickupLocationData, destinationLocationData, carTypeData, alternateInvoiceData } = useAppSelector((state) => state.priceCalculator);
  useAppSelector(selectLanguage);

  // Helper for displaying date + 2h interval
  const formatDateAndInterval = (date: string, hour: [string | null, string | null]) => {
    if (!date || !hour) return '';
    const [year, month, day] = date.split('-');
    const formattedTime = (pickupLocationData.startHour[0] && pickupLocationData.startHour[1])
      ? formatDates(new Date(hour[0] as string), new Date(hour[1] as string))
      : '';
    return `${day}.${month}.${year} ${formattedTime}`;
  };

  const getVehicleTypeLabel = (carType: string): string => {
    switch (carType) {
      case "2":
        return strings.form.vehicleDetails.vehicleTypeOptions.car;
      case "3":
        return strings.form.vehicleDetails.vehicleTypeOptions.van;
      case "4":
        return strings.form.vehicleDetails.vehicleTypeOptions.lightTruck;
      case "LKW7.5":
        return strings.form.vehicleDetails.vehicleTypeOptions.truck;
      case "5":
        return strings.form.vehicleDetails.vehicleTypeOptions.motorcycle;
      case "7":
        return strings.form.vehicleDetails.vehicleTypeOptions.rv;
      case "6":
        return strings.form.vehicleDetails.vehicleTypeOptions.lightTrailer;
      case "Anhänger750.5":
        return strings.form.vehicleDetails.vehicleTypeOptions.trailer;
      case "8":
        return strings.form.vehicleDetails.vehicleTypeOptions.bus;
      default:
        return carType;
    }
  };

  const getSpecialOptionsLabels = (options: string[]): string[] => {
    return options.map(option => {
      switch (option) {
        case "RKZ":
          return strings.form.vehicleDetails.vehicleAdditonalServicesOptions.redLicensePlates;
        case "AW":
          return strings.form.vehicleDetails.vehicleAdditonalServicesOptions.basicDetailing;
        case "FEW":
          return strings.form.vehicleDetails.vehicleAdditonalServicesOptions.vehicleTeaching;
        default:
          return option;
      }
    });
  };

  return (
    <div className="relative">
      <div 
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-md transition-all duration-300 overflow-hidden`}
      >
        <div className={`p-6 space-y-6`}>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            {strings.form.orderSummary.details}
          </h2>

          {/* Contact Information */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
              {strings.form.contactData.title}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.orderSummary.name}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{userInfoData.firstName} {userInfoData.lastName}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.contactData.email}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{userInfoData.email}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.contactData.phone}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{userInfoData.phone}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.contactData.address}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{userInfoData.address}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.orderSummary.city}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{userInfoData.zipcode} {userInfoData.city}</span>
              </div>
            </div>
          </div>

          {/* Pickup Location */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
              {strings.form.pickupLocation.title}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.orderSummary.streetNumber}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{pickupLocationData.streetNumber}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.orderSummary.city}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{pickupLocationData.zip} {pickupLocationData.city}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.orderSummary.country}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{pickupLocationData.country}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.pickupLocation.date}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">
                  {formatDateAndInterval(pickupLocationData.date, pickupLocationData.startHour)}
                </span>
              </div>
              {pickupLocationData.companyName && (
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.orderSummary.companyName}:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{pickupLocationData.companyName}</span>
                </div>
              )}
              {pickupLocationData.contactPerson && (
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.orderSummary.contactPerson}:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{pickupLocationData.contactPerson}</span>
                </div>
              )}
            </div>
          </div>

          {/* Destination Location */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
              {strings.form.destinationLocation.title}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.orderSummary.streetNumber}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{destinationLocationData.streetNumber}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.orderSummary.city}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{destinationLocationData.zip} {destinationLocationData.city}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.orderSummary.country}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{destinationLocationData.country}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.destinationLocation.date}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">
                  {formatDateAndInterval(destinationLocationData.date, destinationLocationData.startHour)}
                </span>
              </div>
              {destinationLocationData.companyName && (
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.orderSummary.companyName}:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{destinationLocationData.companyName}</span>
                </div>
              )}
              {destinationLocationData.contactPerson && (
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.orderSummary.contactPerson}:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{destinationLocationData.contactPerson}</span>
                </div>
              )}
            </div>
          </div>

          {/* Vehicle Details */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
              {strings.form.vehicleDetails.title}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.vehicleDetails.vehicleMake}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{carTypeData.make}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.vehicleDetails.vehicleModel}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{carTypeData.model}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.vehicleDetails.vehicleType}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{getVehicleTypeLabel(carTypeData.carType)}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.vehicleDetails.vehicleLicensePlate}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{carTypeData.licensePlate}</span>
              </div>
              {carTypeData.chassisNumber && (
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.vehicleDetails.chassisNumber}:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{carTypeData.chassisNumber}</span>
                </div>
              )}
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.vehicleDetails.vehicleRegistered}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{carTypeData.carRegistered ? strings.form.vehicleDetails.vehicleRegistered : 'Nein'}</span>
              </div>
              {carTypeData.specialOptions && carTypeData.specialOptions.length > 0 && (
                <div className="md:col-span-2">
                  <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.vehicleDetails.vehicleAdditonalServices}:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{getSpecialOptionsLabels(carTypeData.specialOptions).join(', ')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Alternate Invoice Data */}
          {alternateInvoiceData && (
            <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                Rechnungsempfänger
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.orderSummary.companyName}:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{alternateInvoiceData.name}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.orderSummary.streetNumber}:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{alternateInvoiceData.streetNumber}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.orderSummary.city}:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{alternateInvoiceData.zip} {alternateInvoiceData.city}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">{strings.form.orderSummary.country}:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{alternateInvoiceData.country}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SummaryDisplay;